import { useContext } from 'react';
import { Placeholder } from 'react-bootstrap';
import PropTypes from 'prop-types';
import i18nContext from 'components/i18n-context';
import { TariffInputControl } from 'components/TariffInputs/TariffInputControl';
import { hasRole, TARIFF_GROUPS_UPDATE } from 'services/roles';
import { formatDateTime, isCryptoProvider } from 'services/utils';
import Button from 'uikit/Button/Button';
import { CheckBox } from 'uikit/CheckBox/CheckBox';
import './TariffGroupDetailsTableRowContent.scss';

export const TariffGroupDetailsTableRowContent = ({
  isLoading,
  isUpdateTariffGroup,
  tariffIndex,
  tariffData,
  handleChangeTariffGroupStatus,
  handleChangeTariffValue,
  handleDeleteTariff
}) => {
  const i18n = useContext(i18nContext);

  const getTariffLabel = (tariff) => {
    const transactionalFields = {
      transactional: i18n.getMessage(tariff.transactional ? 'no' : 'yes'),
      systemTariffComment: tariff?.systemTariffComment,
      transactionType: i18n.getMessage(`transactionType.${tariff.transactionType}`),
      transferProvider: i18n.getMessage(`paymentProvider.${tariff.transferProvider}`),
      transferType: i18n.getMessage(`transferType.${tariff.transferType}`),
      paymentMethod: i18n.getMessage(`paymentMethod.${tariff.paymentMethod}`),
      currency: tariff.currency,
      network: tariff.network
    };

    const transactionalFieldsCreate = {
      transactionType: i18n.getMessage(`transactionType.${tariff.transactionType}`),
      transferProvider: i18n.getMessage(`paymentProvider.${tariff.transferProvider}`),
      transferType: i18n.getMessage(`transferType.${tariff.transferType}`),
      paymentMethod: i18n.getMessage(`paymentMethod.${tariff.paymentMethod}`),
      currency: tariff.currency,
      network: tariff.network
    };

    const scheduledFields = {
      systemTariffType: tariff?.systemTariffKey,
      systemTariffComment: tariff?.systemTariffComment,
      transactional: i18n.getMessage(tariff.transactional ? 'no' : 'yes'),
      scheduled: i18n.getMessage(tariff.scheduled ? 'yes' : 'no'),
      currency: tariff.currency
    };

    const info =
      !isUpdateTariffGroup && !tariff.transactional && !tariff.systemTariffKey
        ? transactionalFieldsCreate
        : tariff.transactional
          ? transactionalFields
          : scheduledFields;

    return Object.entries(info)
      .filter(([, value]) => value)
      .map(([key, value], index) => (
        <div key={'tariff-label-part-' + index}>
          {i18n.getMessage(`tariffGroupDetails.labelType.${key}`)}: <strong>{value}</strong>
        </div>
      ));
  };

  const getTariffInfoBlock = (
    tariffBlockInfo,
    isTariffInfo = false,
    isTariffValue = false,
    isTariffCreatedAtAndUpdatedAt = false,
    isStatus = false,
    isDeleteTariff = false
  ) => {
    if (isLoading) {
      return (
        <Placeholder as={'p'} animation={'glow'}>
          <Placeholder className={'tariff-group-details-table-loader-placeholder'} xs={12} bg={'secondary'} />
        </Placeholder>
      );
    } else if (isTariffInfo) {
      return getTariffLabel(tariffData);
    } else if (isTariffValue) {
      return (
        <TariffInputControl
          tariff={tariffData}
          tariffIndex={tariffIndex}
          handleChangeValue={handleChangeTariffValue}
          editable={true}
          isCryptoProvider={isCryptoProvider(tariffData.transferProvider)}
        />
      );
    } else if (isTariffCreatedAtAndUpdatedAt) {
      return (
        <div className={'tariff-group-details-table-date-and-time'}>
          Created:
          <br /> {formatDateTime(tariffData.createdAt)} <br />
          <br />
          Updated:
          <br /> {formatDateTime(tariffData.updatedAt)}
        </div>
      );
    } else if (isStatus) {
      return (
        <div className={'tariff-group-details-table-status-wrapper'}>
          <CheckBox
            className={'tariff-group-details-table-status-checkbox'}
            name={'hidden'}
            isActive={tariffBlockInfo}
            isReverseView={true}
            showCheckBox={true}
            onChange={() => handleChangeTariffGroupStatus(tariffIndex, !tariffBlockInfo)}
            value={i18n.getMessage('tariffGroupDetails.table.rowContent.status.enabled')}
            disabled={isLoading || !hasRole(TARIFF_GROUPS_UPDATE)}
          />
        </div>
      );
    } else if (isDeleteTariff) {
      return (
        <Button
          onClick={() => handleDeleteTariff(isUpdateTariffGroup ? tariffData.id : tariffIndex)}
          type={'primary'}
          size={'small'}
          isDisabled={!!tariffData.systemTariffKey || isLoading}
        >
          {i18n.getMessage('tariffGroupDetails.table.button.delete')}
        </Button>
      );
    } else {
      return <p>{tariffBlockInfo}</p>;
    }
  };

  return (
    <tr>
      <td className={'tariff-group-details-table-cell'}>{getTariffInfoBlock(tariffData, true)}</td>
      <td className={'tariff-group-details-table-cell'}>{getTariffInfoBlock(tariffData, false, true)}</td>
      {isUpdateTariffGroup && (
        <td className={'tariff-group-details-table-cell'}>{getTariffInfoBlock(tariffData, false, false, true)}</td>
      )}
      <td className={'tariff-group-details-table-cell'}>
        {getTariffInfoBlock(tariffData.enabled, false, false, false, true)}
      </td>
      <td className={'tariff-group-details-table-cell'}>
        {getTariffInfoBlock(tariffData, false, false, false, false, true)}
      </td>
    </tr>
  );
};

TariffGroupDetailsTableRowContent.propTypes = {
  isLoading: PropTypes.bool,
  isUpdateTariffGroup: PropTypes.bool,
  tariffIndex: PropTypes.number,
  handleChangeTariffGroupStatus: PropTypes.func,
  handleChangeTariffValue: PropTypes.func,
  handleDeleteTariff: PropTypes.func,
  tariffData: PropTypes.oneOfType([
    PropTypes.shape({
      id: PropTypes.number,
      tariffGroupId: PropTypes.string,
      systemTariffKey: PropTypes.string,
      feeType: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
      scheduled: PropTypes.bool,
      transactional: PropTypes.bool,
      transactionType: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
      transferProvider: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
      transferType: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
      paymentMethod: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
      currency: PropTypes.string,
      network: PropTypes.string,
      fixedValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      percentValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      min: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      max: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      cronExpression: PropTypes.string,
      createdAt: PropTypes.string,
      updatedAt: PropTypes.string,
      enabled: PropTypes.bool,
      individualTariff: PropTypes.bool
    }),
    PropTypes.oneOf([null])
  ])
};
